import React, { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
  FaRegSave,
  FaRegFolderOpen,
  FaRegFile,
  FaRegClipboard,
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  setPhysics, clearSelection, requestFit,
} from '../../features/networkSlice';
import { setNetwork, setMeta } from '../../features/networkUndoSlice';
import { normalizeModel, toLegacyModel } from '../../lib/model';
import { metaFromOpenedFile, stampCreated, stampSaved } from '../../lib/metaDates';
import { convertBlobToBase64 } from '../../lib/network/helpers';
import { downloadText } from '../../lib/network/exportSvg';
import { interactionMatrixText } from '../../lib/network/clipboard';

export default function Handler() {
  const fileInputRef = useRef();

  const dispatch = useDispatch();
  const [diagnostic, setDiagnostic] = useState(null);
  const currentModel = useSelector((state) => state.networkUndo.present);

  // global state
  const nodes = useSelector((state) => state.networkUndo.present.nodes);
  const edges = useSelector((state) => state.networkUndo.present.edges);
  const meta = useSelector((state) => state.networkUndo.present.meta);

  const loadNetwork = (fileEvent) => {
    const file = fileEvent.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (onLoadEvent) => {
        try {
          const warnings = [];
          const model = normalizeModel(JSON.parse(onLoadEvent.target.result), (warning) => warnings.push(warning));
          setDiagnostic(warnings.length ? { severity: 'warning', text: warnings.join('\n') } : null);
          dispatch(setPhysics(false));
          dispatch(clearSelection());
          dispatch(setNetwork({
            ...model,
            meta: metaFromOpenedFile(model.meta, file),
          }));
          dispatch(requestFit());
        } catch (err) {
          setDiagnostic({ severity: 'error', text: err.message });
        }
      };
      reader.readAsText(file);
    }
  };

  const base64EncodeLocalImages = async (node) => {
    if (node.image && node.image.startsWith('blob:http')) {
      const image = await convertBlobToBase64(node.image);
      return { ...node, image };
    }
    return node;
  };

  const saveNetwork = async () => {
    const convertedNodes = await Promise.all(nodes.map(base64EncodeLocalImages));
    const nextMeta = stampSaved(meta || {});
    dispatch(setMeta(nextMeta));
    const model = toLegacyModel({ ...currentModel, nodes: convertedNodes, edges, meta: nextMeta });
    downloadText(JSON.stringify(model, undefined, 2), 'model.json', 'application/json');
  };

  const copyNetworkToClipboard = () => {
    if (window.isSecureContext) {
      navigator.clipboard.writeText(interactionMatrixText(nodes, edges));
    }
  };

  const newNetwork = () => {
    dispatch(setNetwork({ meta: stampCreated({}), nodes: [], edges: [] }));
    dispatch(clearSelection());
    dispatch(requestFit());
  };

  return (
    <>
      {diagnostic && <Alert severity={diagnostic.severity} onClose={() => setDiagnostic(null)}>{diagnostic.text}</Alert>}
      <ToggleButtonGroup size="small" sx={{ ml: 3 }}>
        <ToggleButton value="" title="New" onClick={newNetwork}>
          <FaRegFile size="1.5em" />
        </ToggleButton>
        <ToggleButton value="" title="Open" onClick={() => fileInputRef.current.click()}>
          <FaRegFolderOpen size="1.5em" />
        </ToggleButton>
        <ToggleButton value="" title="Save" onClick={() => saveNetwork()}>
          <FaRegSave size="1.5em" />
        </ToggleButton>
        <ToggleButton value="" title="Copy to clipboard" onClick={() => copyNetworkToClipboard()}>
          <FaRegClipboard size="1.5em" />
        </ToggleButton>
      </ToggleButtonGroup>
      <input
        ref={fileInputRef}
        type="file"
        aria-label="Open model file"
        accept=".json"
        onClick={(event) => {
          // eslint-disable-next-line
          event.target.value = null; // allow same file to be loaded (e.g after "New")
        }}
        onChange={loadNetwork}
        style={{ display: 'none' }}
      />
    </>
  );
}
